/* ================================================================
   region.ts — which market this build is for.

   The site is built once per market. REACT_APP_REGION (default `au`,
   from .env) names a directory under ./regions/ holding that market's
   pack: region.json (facts), content.ts (copy) and seo.json (per-route
   metadata). Everything else in src/ is market-neutral and reads the
   market through this module or src/config/content.ts.

   The require below names the pack with a template literal on purpose.
   webpack inlines process.env.REACT_APP_REGION at build time, so the
   path is a constant: only the selected market is bundled, and a code
   with no directory fails the build with "Module not found" rather than
   quietly serving Australia. scripts/region.js applies the same rule to
   the build scripts and says so in plain words before webpack runs.
   ================================================================ */

import type { RegionProfile } from './market';
import { formatDate } from './market';

export type RegionContent = typeof import('./regions/au/content');

export interface SeoRoute {
  path: string;
  title?: string;
  fullTitle?: string;
  description: string;
}

export interface RegionPack {
  region: RegionProfile;
  seo: { routes: SeoRoute[] };
  content: RegionContent;
}

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { pack }: { pack: RegionPack } = require(`./regions/${process.env.REACT_APP_REGION}`);

if (pack.region.code !== process.env.REACT_APP_REGION) {
  throw new Error(
    `regions/${process.env.REACT_APP_REGION}/region.json declares code "${pack.region.code}"`
  );
}

export const REGION: RegionProfile = pack.region;
export const SEO_ROUTES: SeoRoute[] = pack.seo.routes;
export const CONTENT: RegionContent = pack.content;

/** A calendar date in this market's style, e.g. "Sep 1, 2026". */
export const formatMarketDate = (date: Date) => formatDate(REGION, date);
